import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { FatigueSettings } from "../../types";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { LuChevronDown, LuTriangleAlert } from "react-icons/lu";
import { Separator } from "@/components/ui/separator";
import { Dialog as CustomDialog } from "@/components/Dialog/Dialog";
import { TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tooltip } from "@/components/ui/tooltip";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { IconDatabase } from "@tabler/icons-react";
import { sortMetricItems } from "@/screens/utils/Helpers";

const comparisonPeriodOptions = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  comparisonPeriod: FatigueSettings["comparison_period"];
  setComparisonPeriod: (period: FatigueSettings["comparison_period"]) => void;
  conditions: FatigueSettings["conditions"];
  setConditions: (conditions: FatigueSettings["conditions"]) => void;
  onSave: () => Promise<void>;
  metrics: { key: string; label: string; sub_type?: string }[];
  resetToLastSaved: () => void;
};

export function FatigueSettingsDialog({
  open,
  onOpenChange,
  comparisonPeriod,
  setComparisonPeriod,
  conditions,
  setConditions,
  onSave,
  metrics,
  resetToLastSaved,
}: Props) {
  const [openDropdowns, setOpenDropdowns] = useState<boolean[]>([false, false, false]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingPercentage, setEditingPercentage] = useState<{
    index: number;
    type: "mid" | "high";
    value?: number;
  } | null>(null);
  const [initialState, setInitialState] = useState({
    comparisonPeriod,
    conditions,
  });

  useEffect(() => {
    if (open) {
      setInitialState({
        comparisonPeriod,
        conditions,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const allowSaving = () => {
    if (conditions.some((condition) => !condition.metric_id)) return false;
    if (comparisonPeriod !== initialState.comparisonPeriod) return true;

    if (conditions.length !== initialState.conditions.length) return true;

    return JSON.stringify(conditions) !== JSON.stringify(initialState.conditions);
  };

  const handleSliderChange = (index: number, values: number[]) => {
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      mid_fatigue: values[0],
      high_fatigue: values[1],
    };
    setConditions(newConditions);
  };

  const handlePercentageChange = (index: number, type: "mid" | "high", value: string) => {
    const numValue = Math.min(100, Math.max(0, parseInt(value) || 0));
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      [type === "mid" ? "mid_fatigue" : "high_fatigue"]: numValue,
    };
    setEditingPercentage({ index, type, value: numValue });
  };

  const handlePercentageBlur = (
    index: number,
    type: "mid" | "high",
    e: React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    const newConditions = [...conditions];
    const currentCondition = newConditions[index];
    const newValue =
      editingPercentage?.value ??
      (type === "mid" ? currentCondition.mid_fatigue : currentCondition.high_fatigue);

    if (type === "mid" && newValue > currentCondition.high_fatigue) {
      toast.message(
        <ToastContent
          icon={LuTriangleAlert}
          headingPrefix="Invalid input"
          subheadingPrefix="'Mid Fatigue' cannot be higher than 'High Fatigue'"
          isError={true}
        />,
      );
      e.target.focus();
      return;
    } else if (type === "high" && newValue < currentCondition.mid_fatigue) {
      toast.message(
        <ToastContent
          icon={LuTriangleAlert}
          headingPrefix="Invalid input"
          subheadingPrefix="'High Fatigue' cannot be lower than 'Mid Fatigue'"
          isError={true}
        />,
      );
      e.target.focus();
      return;
    }

    handleSliderChange(index, [
      type === "mid" ? newValue : currentCondition.mid_fatigue,
      type === "high" ? newValue : currentCondition.high_fatigue,
    ]);
    setEditingPercentage(null);
  };

  const handlePercentageKeyDown = (
    index: number,
    type: "mid" | "high",
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const renderPercentageField = (index: number, type: "mid" | "high") => {
    const isEditing =
      editingPercentage?.index === index && editingPercentage?.type === type;
    const value = isEditing
      ? editingPercentage.value
      : type === "mid"
        ? conditions[index]?.mid_fatigue
        : conditions[index]?.high_fatigue;

    if (isEditing) {
      return (
        <input
          type="number"
          min={0}
          max={100}
          value={value}
          onChange={(e) => handlePercentageChange(index, type, e.target.value)}
          onBlur={(e) => handlePercentageBlur(index, type, e)}
          onKeyDown={(e) => handlePercentageKeyDown(index, type, e)}
          className="text-[rgba(2,24,89,0.80)] text-[12px] text-center font-normal leading-[19.157px] tracking-[0.24px] rounded-[4px] border border-border-gray bg-white w-[48px] px-[6px] py-[6px]"
          autoFocus
        />
      );
    }

    return (
      <div
        onClick={() =>
          setEditingPercentage({
            index,
            type,
            value:
              type === "mid"
                ? conditions[index]?.mid_fatigue
                : conditions[index]?.high_fatigue,
          })
        }
        className="h-9 text-[rgba(2,24,89,0.80)] text-[12px] text-center font-normal leading-[19.157px] tracking-[0.24px] rounded-[8px] border border-border-gray bg-white flex w-[48px] px-[6px] py-[6px] items-center justify-center gap-[6px] cursor-pointer"
      >
        {`${value}%`}
      </div>
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetToLastSaved();
    }
    onOpenChange(open);
  };

  return (
    <CustomDialog
      isOpen={open}
      onClose={() => handleOpenChange(false)}
      className="w-[783px] p-0 gap-0 rounded-[12px] border border-border-gray bg-white shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),_0px_1px_3px_0px_rgba(0,0,0,0.10)]"
    >
      <div className="relative">
        <div className="px-[31px] py-[24px]">
          <h2 className="text-brand-primary-new text-[15px] font-medium leading-[18px] tracking-[0.3px]">
            Fatigue Settings
          </h2>
          <p className="mt-[10px] text-brand-primary-600 text-[14px] font-normal leading-[18px] tracking-[0.28px]">
            Set the comparison period and fatigue rules; you can specify both mid and high
            fatigue levels.
          </p>
        </div>

        <div className="px-[31px] space-y-6">
          <div className="space-y-4">
            <label className="text-brand-primary-800 text-[14px] font-medium leading-[19.157px] tracking-[0.28px]">
              Comparison Period
            </label>
            <Select
              value={comparisonPeriod}
              onValueChange={(value: FatigueSettings["comparison_period"]) =>
                setComparisonPeriod(value)
              }
            >
              <SelectTrigger className="rounded-[8px] border-[1px] border-border-gray bg-white shadow-sm flex w-[107px] h-9 px-[14px] py-[6px] justify-between items-center">
                <span className="text-[12px] text-[rgba(2,24,89,0.8)] font-normal leading-[19.157px] tracking-[0.24px]">
                  {
                    comparisonPeriodOptions.find(
                      (option) => option.value === comparisonPeriod,
                    )?.label
                  }
                </span>
              </SelectTrigger>
              <SelectContent className="rounded-[8px]">
                {comparisonPeriodOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-[rgba(2,24,89,0.8)] text-[12px] font-normal leading-[19.157px] tracking-[0.24px]"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-[24px]">
            <div>
              <label className="text-[rgba(2,24,89,0.8)] text-[14px] font-medium leading-[19.157px] tracking-[0.28px]">
                Fatigue Conditions
              </label>
              <div className="flex items-center justify-between">
                <p className="text-[14px] text-[rgba(2,24,89,0.60)] font-normal leading-[19.157px] tracking-[0.28px]">
                  Fatigue triggers when any 2 conditions are met.
                </p>
                <div className="flex items-center gap-[26px]">
                  <div className="flex items-center gap-[11.5px]">
                    <span className="w-[16px] h-[1.5px] rounded-full bg-[#FFE283]"></span>
                    <span className="text-[rgba(2,24,89,0.60)] text-[14px] font-normal leading-[19.157px] tracking-[0.28px]">
                      Mid Fatigue
                    </span>
                  </div>
                  <div className="flex items-center gap-[11.5px]">
                    <span className="w-[16px] h-[1.5px] rounded-full bg-[#FF7274]"></span>
                    <span className="text-[rgba(2,24,89,0.60)] text-[14px] font-normal leading-[19.157px] tracking-[0.28px]">
                      High Fatigue
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-[24px]">
              {[0, 1, 2].map((index) => (
                <div key={index} className="">
                  <div className="flex items-center gap-[12px]">
                    <DropdownMenu
                      open={openDropdowns[index]}
                      onOpenChange={(open) => {
                        const newOpenDropdowns = [...openDropdowns];
                        newOpenDropdowns[index] = open;
                        setOpenDropdowns(newOpenDropdowns);
                      }}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex items-center gap-[6px] min-w-[110px] w-[110px] bg-white justify-between"
                        >
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="truncate text-[12px] text-[rgba(2,24,89,0.8)] font-normal leading-[19.157px] tracking-[0.24px]">
                                  {conditions[index]?.metric_id
                                    ? metrics.find(
                                        (m) => m.key === conditions[index]?.metric_id,
                                      )?.label
                                    : "Select metric"}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {conditions[index]?.metric_id
                                  ? metrics.find(
                                      (m) => m.key === conditions[index]?.metric_id,
                                    )?.label
                                  : "Select metric"}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <LuChevronDown
                            size={14}
                            className="text-brand-primary-800 shrink-0"
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="w-[200px] p-0 rounded-[8px]"
                      >
                        <Command
                          filter={(value, search) => {
                            if (value.toLowerCase().includes(search.toLowerCase()))
                              return 1;
                            return 0;
                          }}
                        >
                          <CommandInput placeholder="Search metrics..." />
                          <CommandEmpty>No metrics found.</CommandEmpty>
                          <CommandGroup className="max-h-[200px] overflow-y-auto">
                            {sortMetricItems(metrics)
                              .filter(
                                (metric) =>
                                  !conditions.some(
                                    (condition, i) =>
                                      i !== index && condition?.metric_id === metric.key,
                                  ),
                              )
                              .map((metric) => (
                                <CommandItem
                                  key={metric.key}
                                  value={metric.label}
                                  onSelect={() => {
                                    const newConditions = [...conditions];
                                    if (!newConditions[index]) {
                                      newConditions[index] = {
                                        metric_id: metric.key,
                                        operator: "increases",
                                        mid_fatigue: 25,
                                        high_fatigue: 50,
                                      };
                                    } else {
                                      newConditions[index] = {
                                        ...newConditions[index],
                                        metric_id: metric.key,
                                      };
                                    }
                                    setConditions(newConditions);
                                    const newOpenDropdowns = [...openDropdowns];
                                    newOpenDropdowns[index] = false;
                                    setOpenDropdowns(newOpenDropdowns);
                                  }}
                                  className="cursor-pointer text-[12px] text-[rgba(2,24,89,0.8)] font-normal leading-[19.157px] tracking-[0.24px]"
                                >
                                  {metric?.sub_type === "EXTERNAL" && (
                                    <IconDatabase
                                      size={16}
                                      color="#02185999"
                                      className="shrink-0 mr-1 relative -top-[1px]"
                                    />
                                  )}
                                  <span>{metric.label}</span>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </Command>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Select
                      value={conditions[index]?.operator || "increases"}
                      onValueChange={(value: "increases" | "decreases") => {
                        const newConditions = [...conditions];
                        if (!newConditions[index]) {
                          newConditions[index] = {
                            metric_id: "",
                            operator: value,
                            mid_fatigue: 25,
                            high_fatigue: 50,
                          };
                        } else {
                          newConditions[index] = {
                            ...newConditions[index],
                            operator: value,
                          };
                        }
                        setConditions(newConditions);
                      }}
                    >
                      <SelectTrigger className="flex items-center gap-[6px] min-w-[110px] w-[110px] h-9 px-[14px] py-[6px] rounded-[8px] border border-border-gray bg-white shadow-sm justify-between text-[12px] text-[rgba(2,24,89,0.8)] font-normal leading-[19.157px] tracking-[0.24px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-[8px]">
                        <SelectItem
                          value="increases"
                          className="text-[12px] text-[rgba(2,24,89,0.8)] font-normal leading-[19.157px] tracking-[0.24px]"
                        >
                          Increases
                        </SelectItem>
                        <SelectItem
                          value="decreases"
                          className="text-[12px] text-[rgba(2,24,89,0.8)] font-normal leading-[19.157px] tracking-[0.24px]"
                        >
                          Decreases
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <Slider
                      value={[
                        conditions[index]?.mid_fatigue || 0,
                        conditions[index]?.high_fatigue || 0,
                      ]}
                      max={100}
                      min={0}
                      step={1}
                      minStepsBetweenThumbs={0}
                      onValueChange={(values) => {
                        const [mid, high] = values;
                        if (mid < high) {
                          handleSliderChange(index, values);
                        }
                      }}
                      className="w-full"
                    />
                    <div className="flex gap-[6px] justify-between">
                      {renderPercentageField(index, "mid")}
                      {renderPercentageField(index, "high")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <span className="text-[rgba(2,24,89,0.50)] text-[12px] font-normal leading-[19.157px] tracking-[0.24px]">
                e.g., Frequency Increases 10% (Mid Fatigue) – 25% (High Fatigue)
              </span>
            </div>
          </div>
        </div>

        <Separator className="w-full mt-[27px]" color="#67749B29" />

        <div className="flex justify-end gap-3 px-[33px] py-[20px]">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
            className="text-brand-primary-new text-[12px] font-medium leading-[19.157px] tracking-[0.24px] bg-white flex px-[14px] py-[6px] items-center gap-[6px]"
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={!allowSaving() || isSaving}
            className="border-0 min-w-[61px] text-white text-[12px] font-medium leading-[19.157px] tracking-[0.24px] disabled:opacity-40 bg-brand-orange hover:bg-hover-brand_orange shadow-sm flex px-4 py-[6px] items-center gap-2"
          >
            {isSaving ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </CustomDialog>
  );
}
